import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import Grid from "@/components/Grid";
import { CreateLoaderInput } from "@/components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Geofences";
import {
  setStatusGeofences,
  getShowGeofences,
  deleteGeofencesData,
} from "@/Api/geofences";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { GeofenceMap } from "../shared/GeofenceMap";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Unidade</TableHead>
            <TableHead>Cargo</TableHead>
            <TableHead>Raio</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                <TableCell>{item?.unity?.name || "-"}</TableCell>
                <TableCell>{item?.position?.name || "Todos"}</TableCell>
                <TableCell>{item?.radius_meters}m</TableCell>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={!!item?.is_active}
                    params={{
                      func: setStatusGeofences,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: !values?.is_active,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-geofence")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-geofence")}>
                      <DeleteBtn
                        params={{
                          func: deleteGeofencesData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-geofence-details")}>
                      <ShowBtn
                        className="sm:max-w-[950px]"
                        labels={{ title: "Visualizar Cerca Virtual" }}
                        params={{
                          func: getShowGeofences,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => {
                          const lat = Number(responseApi?.latitude);
                          const lng = Number(responseApi?.longitude);
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="flex flex-col gap-3">
                                <Grid.Item name="Nome" flex={0}>
                                  {responseApi?.name}
                                </Grid.Item>
                                <Grid.Item name="Unidade" flex={0}>
                                  {responseApi?.unity?.name || "-"}
                                </Grid.Item>
                                <Grid.Item name="Cargo" flex={0}>
                                  {responseApi?.position?.name || "Todos"}
                                </Grid.Item>
                                <Grid.Item name="Raio" flex={0}>
                                  {responseApi?.radius_meters}m
                                </Grid.Item>
                                <Grid.Item name="Latitude" flex={0}>
                                  {responseApi?.latitude}
                                </Grid.Item>
                                <Grid.Item name="Longitude" flex={0}>
                                  {responseApi?.longitude}
                                </Grid.Item>
                              </div>

                              <div className="flex flex-col gap-2">
                                <label className="text-sm font-medium">
                                  Ponto central
                                </label>
                                <div className="h-[420px] md:h-full min-h-[300px] w-full overflow-hidden rounded-md border">
                                  <GeofenceMap
                                    latitude={lat}
                                    longitude={lng}
                                    radiusMeters={Number(responseApi?.radius_meters) || 0}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }}
                        loader={<CreateLoaderInput lines={5} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={6}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
